import React from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  Grid,
  GridProps,
  Tooltip,
  Box,
  Typography,
  SelectChangeEvent,
  type MenuItemProps,
  type BoxProps,
} from '@mui/material';
import { useField } from 'formik';
import type { DropdownOption } from 'types/common/DropdownOption';
import { useSchemaContext } from './SparkForm';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useResize } from 'hooks/common';
import { SparkImageLoader } from 'sharedComponents/UI';

type SparkDropdownProps = {
  label: string;
  name: string;
  placeholder?: string;
  subHeaderText?: string;
  size?: GridProps['xs'];
  containerStyle?: GridProps['sx'];
  children: DropdownOption[];
  helpText?: string;
  onChange?: (value: DropdownOption['value']) => void;
  imgStyles?: React.CSSProperties | undefined;
  disabled?: boolean;
  required?: boolean;
  disableGutter?: boolean;
  menuItemProps?: Omit<MenuItemProps, 'open'>;
  menuItemContainerStyles?: BoxProps['sx'];
};

const SparkDropdown = ({
  label,
  name,
  placeholder = '',
  children,
  size,
  helpText = '',
  containerStyle,
  onChange,
  imgStyles = {},
  subHeaderText,
  disabled,
  menuItemProps = {},
  required = false,
  disableGutter = false,
  menuItemContainerStyles = {},
}: SparkDropdownProps) => {
  const [field, meta, helper] = useField<string>(name);
  const { validationSchema } = useSchemaContext();
  const { isMobile } = useResize();
  const emptyOption: DropdownOption = {
    label: placeholder || 'Please select',
    value: '',
  };
  const options = [emptyOption, ...children];
  const isFieldRequired = React.useMemo(() => {
    try {
      const fieldSchema = validationSchema.describe().fields[name];
      //@ts-ignore
      const isRequiredSchema = fieldSchema.tests.find(
        //@ts-ignore
        (test) => test.name === 'required'
      );

      if (!!isRequiredSchema) {
        return true;
      }

      if ('optional' in fieldSchema) {
        return !fieldSchema.optional;
      }
      return false;
    } catch {
      return false;
    }
  }, [name, validationSchema]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    helper.setValue(value);
    onChange && onChange(value);
  };

  const isError = meta.touched && !!meta.error;
  const defaultSize = size || (isMobile ? 12 : 6);
  return (
    <Grid item xs={defaultSize} sx={containerStyle}>
      {label && (
        <InputLabel
          sx={{ marginLeft: 1, display: 'flex', gap: 0.5, fontSize: 16 }}
        >
          <span>{(isFieldRequired || required) && '*'}</span>
          {label}
          {helpText && (
            <Tooltip title={helpText} placement='top' arrow>
              <InfoOutlinedIcon fontSize='small' />
            </Tooltip>
          )}
        </InputLabel>
      )}
      {subHeaderText && (
        <Typography
          color='#A3A3A3'
          marginLeft={1.6}
          fontSize={12}
          textAlign='left'
        >
          {subHeaderText}
        </Typography>
      )}
      <FormControl
        variant='outlined'
        error={meta.touched && meta.error ? true : false}
        sx={{ width: '100%' }}
      >
        <Select
          {...field}
          id={name}
          placeholder={placeholder}
          sx={{
            bgcolor: (theme) => theme.palette.common.white,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#6267AC',
            },
            '& fieldset': {
              borderColor: '#EEEEEE',
              borderRadius: '10px',
            },
          }}
          disabled={disabled}
          displayEmpty={true}
          renderValue={(value) => {
            const option = options.find((option) => option.value === value);
            return (
              <Box display='flex' alignItems='center' gap={2}>
                {(!!option?.iconUrl || !!option?.icon) && (
                  <SparkImageLoader
                    style={{ width: 24, ...imgStyles }}
                    src={option?.iconUrl}
                    alt={option?.iconUrl}
                    fallback={option?.icon}
                    loading='lazy'
                  />
                )}
                <Typography
                  color={(theme) =>
                    option?.value === '' ? theme.palette.grey[500] : ''
                  }
                >
                  {option?.label}
                </Typography>
              </Box>
            );
          }}
          onChange={handleChange}
          MenuProps={{
            sx: { maxWidth: '90%' },
            PaperProps: {
              sx: {
                maxHeight: 300,
                overflowY: 'auto',
                scrollbarWidth: 'none',
              },
            },
          }}
        >
          {children.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              dense
              divider
              {...menuItemProps}
            >
              <Box
                display='flex'
                p={1}
                gap={1}
                sx={menuItemContainerStyles}
                alignItems='center'
              >
                {(!!option.iconUrl || !!option.icon) && (
                  <Box>
                    <SparkImageLoader
                      style={{ width: 18, ...imgStyles }}
                      src={option.iconUrl}
                      alt={option.iconUrl}
                      fallback={option.icon}
                      loading='lazy'
                    />
                  </Box>
                )}
                <Box>
                  <Typography fontFamily='Mont-semibold' fontSize={14}>
                    {option.label}
                  </Typography>
                  {!!option.description && (
                    <Typography
                      fontSize={14}
                      sx={{
                        textWrap: 'wrap',
                        color: '#4A4A4A',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        lineHeight: 'normal',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                      }}
                    >
                      {option.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
        {!disableGutter && (
          <FormHelperText sx={{ marginBottom: 0.5 }}>
            {isError ? meta.error : ' '}
          </FormHelperText>
        )}
      </FormControl>
    </Grid>
  );
};

export default SparkDropdown;
